import { formatPrice } from "../../../../../../common/helpers/formatPrice";
import { extractCurrencyInfo } from "../../../../../../common/helpers/currency";
import type {
  RawCartTotals,
  RawShippingRate,
  RawShippingRatePackage,
} from "../../../../../../types";
import type { TransformedCartTotals } from "../../../types/cartApiTypes";

/**
 * A pure helper function to transform the main cart totals object.
 * @param totals The raw totals object from the WC Store API.
 * @param shippingRatesPackages The raw shipping rates packages from the cart response.
 * @returns A transformed totals object with formatted prices and shipping status.
 */
export const transformCartTotals = (
  totals: RawCartTotals,
  shippingRatePackages: RawShippingRatePackage[]
): TransformedCartTotals => {
  let shippingStatus: "free" | "pickup" | "paid" | "none" = "none";
  let selectedShippingRate: RawShippingRate | undefined;

  // Extract currency info directly from the totals object using the helper
  const currencyInfo = extractCurrencyInfo(totals);

  // Find the selected shipping rate across all packages
  if (shippingRatePackages && shippingRatePackages.length > 0) {
    for (const pkg of shippingRatePackages) {
      selectedShippingRate = pkg.shipping_rates.find((rate) => rate.selected);
      if (selectedShippingRate) {
        break;
      }
    }
  }

  if (selectedShippingRate) {
    if (selectedShippingRate.method_id === "free_shipping") {
      shippingStatus = "free";
    } else if (selectedShippingRate.method_id === "pickup_location") {
      shippingStatus = "pickup";
    } else if (parseFloat(selectedShippingRate.price) > 0) {
      shippingStatus = "paid";
    } else {
      // Fallback for any other method with a price of 0
      shippingStatus = "free";
    }
  } else {
    // If no rate is selected, check if any rates were available at all.
    const hasAnyShippingMethods = shippingRatePackages?.some(
      (pkg) => pkg.shipping_rates.length > 0
    );
    if (!hasAnyShippingMethods) {
      shippingStatus = "none";
    }
    // If methods exist but none are selected, 'none' remains a safe default.
  }

  return {
    totalItems: formatPrice(totals.total_items, currencyInfo),
    totalItemsTax: formatPrice(totals.total_items_tax, currencyInfo),
    totalFees: formatPrice(totals.total_fees, currencyInfo),
    totalFeesTax: formatPrice(totals.total_fees_tax, currencyInfo),
    totalDiscount: formatPrice(totals.total_discount, currencyInfo),
    totalDiscountTax: formatPrice(totals.total_discount_tax, currencyInfo),
    totalShipping: formatPrice(totals.total_shipping, currencyInfo),
    totalShippingTax: formatPrice(totals.total_shipping_tax, currencyInfo),
    totalPrice: formatPrice(totals.total_price, currencyInfo),
    totalTax: formatPrice(totals.total_tax, currencyInfo),
    taxLines: totals.tax_lines.map((line) => ({
      ...line,
      price: formatPrice(line.price, currencyInfo),
    })),
    rawTotalPrice: totals.total_price,
    rawTotalTax: totals.total_tax,
    hasFees: parseInt(totals.total_fees, 10) > 0,
    hasDiscount: parseInt(totals.total_discount, 10) > 0,
    hasTax: parseInt(totals.total_tax, 10) > 0,
    shippingStatus: shippingStatus,
  };
};
